import { Button, Flex, Icon, Label, ModalManagerContext, Text, Image, Link, Grid } from '@pega/cosmos-react-core';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import { ConfirmationAlert } from '../../Utilities/CustomModal';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import { authorizeTwitterAccount, deleteAccount, getAccountDetails } from './TwitterApis';
import * as Logos from '../../../constants/images';
import { appState, confirmationAlertProps } from '../../../interfaces/common';
import useLocalTranslation from '../../../hooks/useLocalTranslation';

type AccountDetails = {
  name: string;
  handle: string;
  authorized: boolean;
};

const Twitter: React.FC<any> = (props) => {
  const { id: connectionId }: any = useParams();
  const sessionId = useSelector((state: appState) => state.sessionId);
  const initialState: AccountDetails = {
    name: '',
    handle: '',
    authorized: false,
  };
  const [accountDetails, setaccountDetails] = useState<AccountDetails>(initialState);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const { create } = useContext(ModalManagerContext);
  const modalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const deleteConfirmation = () => {
    modalMethods.current = create(
      ConfirmationAlert,
      {
        pageName: accountDetails?.handle,
        deleteChannel: deleteChannel,
        ...props,
      },
      { alert: true },
    );
  };

  const reauthorize = async () => {
    dispatch(updateLoaderDetails(true));
    const errorResponse = await authorizeTwitterAccount(sessionId);
    if (errorResponse) {
      dispatch(updateBannerInfo(translate('failed_to_authorize_twitter'), 'urgent'));
    }
    dispatch(updateLoaderDetails(false));
  };

  const fetchAccountDetails = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await getAccountDetails(sessionId, connectionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      setaccountDetails(response?.data);
    } else {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId, connectionId, navigate]);

  const deleteChannel = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await deleteAccount(connectionId, sessionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      navigate('/');
      dispatch(updateBannerInfo(`Twitter ${translate('account_deleted')}`, 'success'));
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    } else {
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
  }, [sessionId, connectionId, dispatch, navigate, translate]);

  useEffect(() => {
    fetchAccountDetails();
  }, [fetchAccountDetails]);

  const headerElement = () => {
    return (
      accountDetails?.name && (
        <Text variant="h1" as="h1">
          {accountDetails?.name}
        </Text>
      )
    );
  };

  return (
    <SectionRenderTemplate header={headerElement()}>
      {accountDetails?.name && (
        <Flex container={{ direction: 'column', itemGap: 2 }}>
          <Grid
            container={{
              cols: 'repeat(3, 1fr)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('connection')}</Label>
            <Flex
              container={{
                direction: 'row',
                itemGap: 1,
                alignItems: 'center',
              }}
            >
              <Text variant="secondary">{translate('Twitter')}</Text>
              <Image alt="Twitter" src={Logos.TwitterLogo} width="30px" height="40px" />
            </Flex>
          </Grid>

          <Grid
            container={{
              cols: 'repeat(2, 33% 67%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('profile_link')}</Label>
            <Flex>
              <Link
                href={`https://twitter.com/${accountDetails?.handle}`}
                target="_blank"
              >{`https://twitter.com/${accountDetails?.handle?.replace(/@/g, '')}`}</Link>
            </Flex>
          </Grid>

          <Grid
            container={{
              cols: 'repeat(2, 33% 67%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('authorized')}</Label>
            <Flex
              container={{
                direction: 'row',
                itemGap: 1,
                alignItems: 'center',
              }}
            >
              <Icon
                style={{
                  color: accountDetails?.authorized ? 'green' : 'red',
                  fontSize: '1.5rem',
                }}
                name={accountDetails?.authorized ? 'check' : 'times'}
              ></Icon>
              {!accountDetails?.authorized && <Button onClick={reauthorize}>{translate('re_authorize')}</Button>}
            </Flex>
          </Grid>

          <Flex style={{ paddingTop: '0.5rem' }}>
            <Button onClick={deleteConfirmation}>{translate('delete_connection_button')}</Button>
          </Flex>
        </Flex>
      )}
    </SectionRenderTemplate>
  );
};

export default Twitter;
